// Single source for the workspace shell: sidebar groups, the command
// palette's page list and the top-bar breadcrumb. The sidebar follows
// docs/design/INFORMATION_ARCHITECTURE.md §4: reps see seven destinations,
// managers add the floor and the team, admins add the organization.
//
// `role` is the normalized context role from UserRoleContext (agent, fronter,
// closer, manager, admin; super_admin reads as admin there).

import { ORGANIZATION_SECTIONS, findOrgSection, orgSectionPath } from "../organization/sections";

const REPS = ["agent", "fronter", "closer"];
const MANAGERS = ["manager", "admin"];
const ADMINS = ["admin"];
const EVERYONE = [...REPS, ...MANAGERS];

/** The IA destinations (INFORMATION_ARCHITECTURE.md §4). */
export const IA_ROUTES = {
  calls: "/calls",
  floor: "/floor",
  team: "/team",
  organization: "/organization",
};

/** Who sees Calls: everyone (IA §4). Reps see their own calls there. */
const CALLS_ROLES = EVERYONE;

/**
 * Who sees Companies and Deals: everyone (IA §4). Reps read them; the API
 * keeps writes and /customers manager+ (router.ex :manager_up), so pages hide
 * those controls for reps (utils/contactPermissions.js canManageCrm).
 */
const CRM_ROLES = EVERYONE;

/** Can this user open company and deal records? Takes the account role. */
export const canOpenCrm = (role) => CRM_ROLES.includes(role === "super_admin" ? "admin" : role);

/** The full-screen Live view for a call started outside a session. */
export const FOCUS_VIEW_PATH = "/session/focus";

/** Is this context role a rep (agent, fronter or closer)? */
export const isRepRole = (role) => REPS.includes(role);

/**
 * Can the signed-in user see an entry gated by `roles`?
 * `role` is the normalized context role (super_admin reads as admin there),
 * so the authenticated user's own role decides super-admin-only entries.
 */
export const canSeeRoles = (roles, role, user) =>
  !roles ||
  roles.includes(role) ||
  (user?.role === "super_admin" && roles.includes("super_admin"));

/** Home is /dashboard; the old role dashboards redirect there. */
export const isHomeViewPath = (pathname) => pathname === "/" || pathname === "/dashboard";

/**
 * Organization sections (components/organization/sections.js) for the command
 * palette. The sidebar shows Organization as one entry; its sections live in
 * the page's own section nav, so they are not repeated there.
 */
const ORG_PALETTE_PAGES = ORGANIZATION_SECTIONS.map((section) => ({
  label: section.label,
  path: orgSectionPath(section.id),
  group: "Organization",
  roles: section.roles || ADMINS,
  keywords: section.keywords,
}));

/**
 * Sidebar groups. `match` lists extra path prefixes that keep an item active;
 * `icon` names a key in the sidebar's icon map. Group labels show only when a
 * role sees more than two groups.
 */
export const NAV_GROUPS = [
  {
    label: "Work",
    items: [
      {
        label: "Home",
        path: "/dashboard",
        icon: "home",
        key: "h",
        roles: EVERYONE,
        match: ["/onboarding"],
      },
      { label: "Dialer", path: "/dialer", icon: "phone", key: "d", roles: EVERYONE, match: ["/session"] },
      { label: "Coach", path: "/coach", icon: "coach", key: "o", roles: EVERYONE, exact: true },
      {
        label: "Calls",
        path: IA_ROUTES.calls,
        icon: "calls",
        key: "a",
        roles: CALLS_ROLES,
      },
    ],
  },
  {
    label: "Customers",
    items: [
      { label: "Contacts", path: "/contacts", icon: "contacts", key: "n", roles: EVERYONE },
      {
        label: "Companies",
        path: "/companies",
        icon: "building",
        key: "m",
        roles: CRM_ROLES,
      },
      { label: "Deals", path: "/deals", icon: "deal", key: "l", roles: CRM_ROLES },
    ],
  },
  {
    label: "Manage",
    items: [
      {
        label: "Floor",
        path: IA_ROUTES.floor,
        icon: "floor",
        key: "f",
        roles: MANAGERS,
      },
      {
        label: "Team",
        path: IA_ROUTES.team,
        icon: "team",
        key: "t",
        roles: MANAGERS,
      },
      {
        label: "Campaigns",
        path: "/campaigns",
        icon: "campaign",
        key: "c",
        roles: MANAGERS,
      },
      { label: "Reports", path: "/reports", icon: "reports", key: "r", roles: MANAGERS },
    ],
  },
  {
    label: "Admin",
    items: [
      {
        label: "Organization",
        path: IA_ROUTES.organization,
        icon: "shield",
        roles: ADMINS,
        match: ["/organization/"],
      },
    ],
  },
];

/** Visible sidebar groups for a role. */
export const navGroupsFor = (role, user) =>
  NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => canSeeRoles(item.roles, role, user)),
  })).filter((group) => group.items.length > 0);

/** Pages reachable only from the palette or from inside other pages. */
const EXTRA_PAGES = [
  { label: "Profile", path: "/settings", group: "Account" },
  { label: "Preferences", path: "/settings/preferences", group: "Account" },
  { label: "Audio devices", path: "/settings/audio", group: "Account" },
  { label: "New campaign", path: "/campaigns/new", group: "Manage", roles: MANAGERS },
  { label: "Call reports", path: "/reports/calls", group: "Manage", roles: MANAGERS },
  { label: "Agent performance", path: "/reports/agents", group: "Manage", roles: MANAGERS },
  { label: "AI analysis", path: "/reports/ai", group: "Manage", roles: ADMINS },
  ...ORG_PALETTE_PAGES,
  { label: "Performance", path: "/organization/system/performance", group: "Organization", roles: ADMINS },
];

/** Every page the user can open, flattened for the command palette. */
export const getPaletteRoutes = (role, user) => {
  const seen = new Set();
  const out = [];
  const add = (entry) => {
    const key = `${entry.path}|${entry.label}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(entry);
  };
  for (const group of navGroupsFor(role, user)) {
    for (const item of group.items) {
      add({ label: item.label, path: item.path, group: group.label, icon: item.icon });
    }
  }
  for (const page of EXTRA_PAGES) {
    if (canSeeRoles(page.roles, role, user)) add(page);
  }
  return out;
};

// Personal settings sub-pages, shown under a Settings parent crumb.
const SETTINGS_PAGES = {
  "/settings/preferences": "Preferences",
  "/settings/audio": "Audio devices",
  "/settings/security": "Security",
};

/** Pages whose title is not a sidebar item. */
const TITLE_OVERRIDES = [
  ["/settings", "Settings"],
  ["/onboarding", "Get set up"],
];

/**
 * Breadcrumb trail for the top bar: [{ label, path }]. The last entry is the
 * current page.
 */
export const getBreadcrumbs = (pathname) => {
  if (isHomeViewPath(pathname)) {
    return [{ label: "Home", path: "/dashboard" }];
  }
  if (pathname === "/organization" || pathname.startsWith("/organization/")) {
    const section = findOrgSection(pathname);
    const trail = [{ label: "Organization", path: "/organization" }];
    if (section) trail.push({ label: section.label, path: orgSectionPath(section.id) });
    return trail;
  }
  const settingsLeaf = SETTINGS_PAGES[pathname];
  if (settingsLeaf) {
    return [
      { label: "Settings", path: "/settings" },
      { label: settingsLeaf, path: pathname },
    ];
  }
  if (pathname === "/campaigns/new") {
    return [
      { label: "Campaigns", path: "/campaigns" },
      { label: "New campaign", path: "/campaigns/new" },
    ];
  }
  const override = TITLE_OVERRIDES.find(([prefix]) => pathname.startsWith(prefix));
  if (override) return [{ label: override[1], path: override[0] }];
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if (pathname === item.path || pathname.startsWith(`${item.path}/`)) {
        const trail = [{ label: item.label, path: item.path }];
        if (pathname !== item.path) {
          const leaf = EXTRA_PAGES.find((p) => p.path === pathname)?.label || "Detail";
          trail.push({ label: leaf, path: pathname });
        }
        return trail;
      }
    }
  }
  const extra = EXTRA_PAGES.find((p) => p.path === pathname);
  return [{ label: extra?.label || "NexusDialer", path: pathname }];
};

/** Is a sidebar item active for the current path? */
export const isItemActive = (item, pathname) => {
  if (item.exact) {
    return pathname === item.path || (item.match || []).some((p) => pathname.startsWith(p));
  }
  if (pathname === item.path || pathname.startsWith(`${item.path}/`)) return true;
  return (item.match || []).some((p) => pathname === p || pathname.startsWith(p));
};

/**
 * Screens that render their own call view and wrap-up, so the dock stays
 * quiet there. The Dialer page adopts any tracked call and wraps it up
 * itself. Session routes (pn/session, including the Focus view) mount
 * outside the workspace layout, so the dock is not on screen there at all;
 * they are listed for completeness, not because the dock defers to a path.
 */
export const isSessionPath = (pathname = "") =>
  pathname === "/session" || pathname.startsWith("/session/") || pathname === "/dialer";

/**
 * "G then <key>" jumps for the sidebar destinations a role can see, in
 * sidebar order: [{ key, label, path }].
 */
export const navSequencesFor = (role, user) =>
  navGroupsFor(role, user)
    .flatMap((group) => group.items)
    .filter((item) => item.key)
    .map((item) => ({ key: item.key, label: item.label, path: item.path }));
